import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  PreservationMessagePayload,
  PreservationAudienceScope,
  VaultPreservationMessage,
} from '../../../../domain/src/lib/preservation-messages/preservation-message.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class PreservationMessagesService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/preservation-messages';

  list(): Observable<VaultPreservationMessage[]> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(this.basePath, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  active(): Observable<VaultPreservationMessage[]> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/active`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  create(payload: PreservationMessagePayload): Observable<VaultPreservationMessage> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .post<unknown>(this.basePath, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.unwrapMessage(raw)));
  }

  update(
    id: string,
    payload: PreservationMessagePayload,
  ): Observable<VaultPreservationMessage> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .patch<unknown>(`${this.basePath}/${encodeURIComponent(id)}`, payload, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.unwrapMessage(raw)));
  }

  delete(id: string): Observable<unknown> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete(`${this.basePath}/${encodeURIComponent(id)}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  private normalizeList(raw: unknown): VaultPreservationMessage[] {
    let arr: unknown[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object') {
      const r = raw as Record<string, unknown>;
      const nested = r['items'] ?? r['messages'] ?? r['data'];
      if (Array.isArray(nested)) arr = nested;
    }
    return arr.map((item, i) => this.toMessage(item, i));
  }

  private unwrapMessage(raw: unknown): VaultPreservationMessage {
    if (raw && typeof raw === 'object' && 'data' in raw) {
      return this.toMessage((raw as Record<string, unknown>)['data'], 0);
    }
    return this.toMessage(raw, 0);
  }

  private toMessage(item: unknown, fallbackIndex: number): VaultPreservationMessage {
    if (!item || typeof item !== 'object') {
      return {
        id: `unknown-${fallbackIndex}`,
        title: 'Mensaje',
        message: '',
        audience_scope: 'all',
        is_active: true,
        created_at: new Date().toISOString(),
      };
    }
    const o = item as Record<string, unknown>;
    const scopeRaw = o['audience_scope'];
    const scope: PreservationAudienceScope =
      scopeRaw === 'role' || scopeRaw === 'tenant' ? scopeRaw : 'all';
    return {
      id: String(o['id'] ?? `row-${fallbackIndex}`),
      title: String(o['title'] ?? ''),
      message: String(o['message'] ?? ''),
      audience_scope: scope,
      audience_role: o['audience_role'] != null ? String(o['audience_role']) : null,
      tenant_id: o['tenant_id'] != null ? String(o['tenant_id']) : null,
      is_active: Boolean(o['is_active'] ?? true),
      starts_at: o['starts_at'] != null ? String(o['starts_at']) : null,
      ends_at: o['ends_at'] != null ? String(o['ends_at']) : null,
      created_at: String(o['created_at'] ?? new Date().toISOString()),
      updated_at: o['updated_at'] != null ? String(o['updated_at']) : null,
    };
  }
}
